'use client'

import React, { useEffect } from 'react'
import Button from '../../UiElements/Button'
//import createTargetObjectCanvas from "../utils/drawSection/createTargetObjectCanvas";

import Form, { FormGeneratorHandles } from '@/components/Form'
import createSectionFormItems from '../forms/sectionFormItems'
import DeleteAbsoluteButton from '@/components/UiElements/DeleteAbsoluteButton'
import Alert from '@/components/UiElements/Alert'
import ImageComponent from '@/components/UiElements/ImageComponent'

interface SectionFormProps {
  section?: any
  isOpen?: boolean
  close: () => void
  onUpdate: (section: any) => void

  sectionData: any
  sectionList: any[]
  profileType?: string
}

const SectionForm: React.FC<SectionFormProps> = ({
  section = {},
  isOpen,
  close,
  onUpdate,
  sectionData = {},
  profileType,
}) => {
  const sectionFormItems = React.useMemo(() => createSectionFormItems({ profileType }), [profileType])

  const formRef = React.useRef<FormGeneratorHandles>(null)

  /**
   * The current change handler. `applyFrameEdit` below is held by the viewer across
   * renders, so reaching the handler through a ref is what keeps it from calling a stale
   * `onUpdate`.
   */
  const changeRef = React.useRef<(data: Record<string, any>) => void>(() => {})

  /**
   * Apply a drag from the 3D gizmo back into the form.
   *
   * Registered on the viewer because the gizmo lives in the scene, not the React tree —
   * the same global-bus arrangement the rest of this screen already uses for the viewer.
   * Width and height go to their own fields rather than into the placement: they are
   * `default_size` on the section under either section model, and duplicating them would
   * create two sources of truth that disagree after an edit.
   */
  React.useEffect(() => {
    const viewer = (window as any).threeViewer
    if (!viewer) return

    viewer.applyFrameEdit = (frame: any) => {
      const form = formRef.current
      if (!form || !frame) return

      // setData + the change handler, NOT updateValue. updateValue moves React state and
      // stops there — the inputs showed the drag while nothing downstream heard about it,
      // so the overlay never repainted and the parent's section list, which is what gets
      // serialised on save, kept the pre-drag width and height.
      const next = {
        ...(form.getData() ?? {}),
        placement: {
          origin: frame.origin,
          normal: frame.normal,
          up: frame.up,
          depth: frame.depth,
          wrapRadius: frame.wrapRadius,
          maxAngleDeg: frame.maxAngleDeg,
        },
        width: Math.round(frame.width * 10) / 10,
        height: Math.round(frame.height * 10) / 10,
      }
      form.setData(next)
      changeRef.current(next)
    }

    return () => {
      if (viewer.applyFrameEdit) viewer.applyFrameEdit = undefined
    }
  }, [isOpen])

  const handleChangeSection = async (formData: Record<string, any>) => {
    // Sync face dropdown to radialAngle for planar/box projection
    const proj = (window as any).threeViewer?.uvProjection
    if ((proj === 'planar' || proj === 'box') && formData.face) {
      formData.radialAngle = formData.face.value === 'back' ? '180' : '0'
    }

    const validations = await formRef.current?.validate(true, formData)
    onUpdate({
      formData: formData,
      validations,
    })
  }

  changeRef.current = handleChangeSection

  const handleUpdateForm = async (data: any) => {
    if (data.offsetX == null) data.offsetX = '0'
    await formRef.current?.setData(data)
    formRef.current?.updateForm('width', 'max', data?.targetObject?.width)
    formRef.current?.updateForm('height', 'max', data?.targetObject?.height)
    formRef.current?.updateForm('offsetY', 'max', data?.targetObject?.height * 2)
    handleChangeSection(data)
  }

  const handleDeleteSection = async () => {
    const form = formRef.current?.getData()
    Alert.showAlert({
      title: 'Delete Section',
      isCancelable: true,
      danger: true,
      button: 'Delete!',
      message: (
        <div className={'flex flex-col items-center justify-center '}>
          {form?.thumbnail?.string && (
            <div className={'border border-gray-300 p-sm rounded-md my-md'}>
              <ImageComponent src={form.thumbnail.string} className={'!w-[100px] !h-[100px]'} />
            </div>
          )}
          Are you sure you want to delete the section &quot;{form?.name}&quot;?
        </div>
      ),
      callBack: async (ok) => {
        if (ok) {
          onUpdate({
            formData: form,
            validations: [],
            isDeleted: true,
          })
        }
      },
    })
  }

  useEffect(() => {
    if (sectionData) handleUpdateForm(sectionData)
  }, [sectionData])

  useEffect(() => {
    formRef.current?.setData({})
  }, [])

  return (
    <div
      className={`bg-white rounded-md  ${
        isOpen ? ' !w-[350px] !h-auto' : '!w-[0px] !h-0'
      }  flex flex-col gap-md relative overflow-hidden`}
    >
      <div className={'p-md'}>
        <div className={' flex flex-row justify-center item-center h-[27px] '}>
          <div className={'flex flex-grow item-center text-title-lg capitalize'}>Section</div>

          <Button
            onClick={close}
            className={
              'font-icon-solid !w-[37px] !h-[37px] overflow-hidden !items-center !justify-center flex text-[18px] !p-0 relative top-[-5px] right-[-10px]'
            }
            type={'link'}
          >
            
          </Button>
        </div>
      </div>

      <div className={` transition-all  relative duration-500 max-h-calc(100vh-350px)`}>
        <div className="flex items-start gap-[30px]  flex-row transition-all duration-500 ">
          <div className={'w-[350px] px-md'}>
            <div className={'grid grid-cols-1 gap-md !w-[100%] pb-[20px] '}>
              <Form
                ref={formRef}
                items={sectionFormItems}
                defaultValues={section}
                onChange={handleChangeSection}
                liveValidate
              />
              <DeleteAbsoluteButton
                label={'Delete Section'}
                className={'!relative !w-full !h-[45px] px-xl col-span-12'}
                onClick={handleDeleteSection}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SectionForm
